import { type FC, type ReactNode, useEffect, useId, useState } from "react";

interface AnimatedProgressBarProps {
  /** 0-100 display position for the fill. */
  displayPosition: number;
  /** Whether to show the glow at the leading edge. Default: true. */
  showGlow?: boolean;
  /** Background content rendered inside the track (e.g., gradient segments). */
  children: ReactNode;
}

export const AnimatedProgressBar: FC<AnimatedProgressBarProps> = ({
  displayPosition,
  showGlow = true,
  children,
}) => {
  const shimmerId = useId();
  const [isAnimated, setIsAnimated] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const fillClipPath = isAnimated
    ? `inset(0 ${100 - displayPosition}% 0 0)`
    : "inset(0 100% 0 0)";
  const unfillClipPath = isAnimated
    ? `inset(0 0 0 ${displayPosition}%)`
    : "inset(0 0 0 0)";
  const clipTransition = "clip-path 1.8s cubic-bezier(0.22, 1, 0.36, 1)";

  return (
    <div className="relative h-7 overflow-hidden rounded-xl ring-1 ring-black/5">
      {children}

      {/* Glass shine on filled portion */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          clipPath: fillClipPath,
          transition: clipTransition,
          background:
            "linear-gradient(to bottom, rgba(255,255,255,0.35) 0%, transparent 45%, rgba(0,0,0,0.06) 100%)",
        }}
      />

      {/* Shimmer sweep on filled portion */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{
          clipPath: fillClipPath,
          transition: clipTransition,
        }}
      >
        <div
          className="absolute inset-y-0"
          style={{
            width: "50%",
            animation: `progress-shimmer-${CSS.escape(shimmerId)} 6s ease-in-out infinite`,
            background:
              "linear-gradient(105deg, transparent 0%, transparent 35%, rgba(255,255,255,0.25) 45%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0.25) 55%, transparent 65%, transparent 100%)",
          }}
        />
      </div>
      <style>{`
        @keyframes progress-shimmer-${CSS.escape(shimmerId)} {
          0% { left: -60%; }
          100% { left: 160%; }
        }
      `}</style>

      {/* Frost overlay on unfilled portion */}
      <div
        className="pointer-events-none absolute inset-0 bg-white/70"
        style={{
          clipPath: unfillClipPath,
          transition: clipTransition,
        }}
      />

      {/* Glow at leading edge */}
      {showGlow ? (
        <div
          className="pointer-events-none absolute inset-y-0 w-14"
          style={{
            left: isAnimated ? `${displayPosition}%` : "0%",
            transform: "translateX(-60%)",
            background:
              "radial-gradient(ellipse at center, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.4) 40%, transparent 70%)",
            filter: "blur(10px)",
            transition: "left 1.8s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      ) : null}
    </div>
  );
};
